import React from 'react';
import Icon from '@ant-design/icons';
import { connect } from 'react-redux';
import cx from 'classnames';

import { ACTION_COLUMN_KEY, ManageMinutesTableColumns, MINUTES_COLUMN_KEY } from './columns';
import { ActionsMenu } from './ActionsMenu';
import { DataTable } from '../../../common/dataTable/dataTable.plgexternal';
import { deleteTerm, getManageMinutesList } from '../../../../slices/accounts/video/management/thunk';
import { EditTableRow } from '../EditTableRow';
import { ExtendedColumnProps } from '../../../../utils/dataTable/column';
import { FetchingStatus } from '../../../../slices/accounts/video/management/interfaces';
import { IAllocationDisplayItem } from '../interfaces';
import { ReactComponent as SxPlus } from '../../../../assets/sightx-icons/svg/sx-plus-circle.svg';
import { ReactComponent as SxRefresh } from '../../../../assets/sightx-icons/svg/sx-refresh.svg';
import { RootState } from '../../../../slices/rootReducer';
import { selectAllocationItemsList, selectDataOfLastTwoStartPeriods, selectDeleteTermRequestStatus, selectListFetchingStatus } from '../../../../slices/accounts/video/management/selectors';
import { SortStatus } from '../../../common/sortButton';
import { TypeDispatchFunction } from '../../../../tsHelpers';
import { fetchAccountCurrentTerm } from '../../../../slices/accounts/video/dashboard/thunk';
import { selectors as accountListSelectors } from '../../../../slices/accounts/list';
import { getAccountRegion, Regions } from '../../../common/domain/accountRegions';

import styles from './index.module.scss';

const HEADER_HEIGHT_PX = 50;
const ROW_HEIGHT_PX = 40;

interface ExternalProps {
  accountId: string;
  editionStarted: () => void;
  editionEnded: () => void;
  disableRowEdition?: boolean;
}

interface IMapStateToProps {
  deleteTermRequestStatus: FetchingStatus;
  fetchListRequestStatus: FetchingStatus;
  indexPositionOfCurrentTermTypeItem: number;
  items: IAllocationDisplayItem[];
  region: Regions;
}

interface IMapDispatchToProps {
  getManageMinutesList: TypeDispatchFunction<typeof getManageMinutesList>;
  deleteTerm: TypeDispatchFunction<typeof deleteTerm>;
  fetchAccountCurrentTerm: TypeDispatchFunction<typeof fetchAccountCurrentTerm>;
}

type Props = IMapStateToProps & IMapDispatchToProps & ExternalProps;

interface IState {
  editRow: {
    editInProgress: boolean;
    rowIndex: number;
    positionInPx: number;
  };
}

class MinutesManagementTableBase extends React.Component<Props, IState> {

  constructor(props: Props) {
    super(props);

    this.state = {
      editRow: {
        editInProgress: false,
        rowIndex: 0,
        positionInPx: 0
      }
    };

    this.handleOnDelete = this.handleOnDelete.bind(this);
    this.handleOnEdit = this.handleOnEdit.bind(this);
    this.handleOnEditCancel = this.handleOnEditCancel.bind(this);
    this.onEditionSucceeded = this.onEditionSucceeded.bind(this);
    this.renderActionsCell = this.renderActionsCell.bind(this);
    this.renderDefaultCell = this.renderDefaultCell.bind(this);
    this.renderMinutesCell = this.renderMinutesCell.bind(this);
  }

  componentDidMount(): void {
    this.props.getManageMinutesList({ accountId: this.props.accountId });
  }

  private onEditionSucceeded() {
    this.props.getManageMinutesList({ accountId: this.props.accountId });
    this.props.fetchAccountCurrentTerm({ accountId: this.props.accountId, region: this.props.region });
    this.handleOnEditCancel();
  }

  private positionEditRow(rowIndex: number) {
    const endPosition = HEADER_HEIGHT_PX + ROW_HEIGHT_PX * rowIndex - 1;

    this.setState((prevState) => {
      return {
        editRow: {
          ...prevState.editRow,
          rowIndex,
          positionInPx: endPosition
        }
      };
    });
  }

  private handleOnDelete(value: IAllocationDisplayItem, record: IAllocationDisplayItem, zeroBasedIndex: number) {
    this.props.deleteTerm({ id: record.id, accountId: this.props.accountId });
  }

  private handleOnEdit(value: IAllocationDisplayItem, record: IAllocationDisplayItem, zeroBasedIndex: number) {
    this.positionEditRow(zeroBasedIndex);
    this.setState((prevState) => {
      return {
        editRow: {
          ...prevState.editRow,
          editInProgress: true
        }
      };
    }, () => {
      this.props.editionStarted();
    });
  }

  private handleOnEditCancel() {
    this.setState({
      editRow: {
        editInProgress: false,
        rowIndex: -1,
        positionInPx: 0
      }
    }, () => {
      this.props.editionEnded();
    });
  }

  private renderEditRow() {
    const { editRow } = this.state;
    const { positionInPx, editInProgress, rowIndex } = editRow;
    const { items } = this.props;
    const isEven = (rowIndex % 2) === 0;
    const item = items[rowIndex];

    if(!item) {
      return null;
    }

    return (
      <div
        style={{ top: `${positionInPx}px` }}
        className={cx(styles.editRowContainer, { [styles.hide]: !editInProgress } )}
      >
        <EditTableRow
          accountId={this.props.accountId}
          itemId={item.id}
          key={item.id}
          minutes={Number(item.display.minutes)}
          onCancel={this.handleOnEditCancel} tone={isEven ? 'light' : 'dark'}
          onEditionSucceded={this.onEditionSucceeded}
          term={{ editable: (item.type === 'term' && item.display.belongsToCurrentTerm), dateStr: item.display.startDate, plainValue: item.display.termRange } }
          type={item.type}
        />
      </div>
    );
  }

  private renderActionsCell(value: IAllocationDisplayItem, record: IAllocationDisplayItem, zeroBasedIndex: number) {
    if (!value) {
      return null;
    }

    const { disableRowEdition } = this.props;
    const { display } = record;
    const { belongsToCurrentTerm } = display;
    const termTypeItemIsFirstItem = this.props.indexPositionOfCurrentTermTypeItem === 0;
    const indexesPositionMatch = zeroBasedIndex === this.props.indexPositionOfCurrentTermTypeItem;
    const isRowEditionInProgress = this.state.editRow.editInProgress;
    const shouldDisabled = !belongsToCurrentTerm || (!termTypeItemIsFirstItem && indexesPositionMatch) || isRowEditionInProgress || disableRowEdition;

    return (
      <>
        <ActionsMenu
          onDelete={this.handleOnDelete.bind(this, value, record, zeroBasedIndex)}
          onEdit={this.handleOnEdit.bind(this, value, record, zeroBasedIndex)}
          disabled={shouldDisabled}
        />
      </>
    );
  }

  private renderMinutesCell(value: IAllocationDisplayItem, record: IAllocationDisplayItem, zeroBasedIndex: number) {
    if (!value) {
      return null;
    }

    const { type, display } = record;

    return (
      <div className={styles.minutesCell}>
        <Icon className={styles.icon} component={type === 'additionalMinutes' ? SxPlus : SxRefresh} />
        <div>{display.minutes}</div>
      </div>
    );
  }

  private renderDefaultCell(value: string, record: IAllocationDisplayItem, zeroBasedIndex: number) {
    return <div className={styles.defaultCellContent}>{value}</div>;
  }

  private getActiveColumns(): ExtendedColumnProps<IAllocationDisplayItem>[] {
    return ManageMinutesTableColumns.map(item => {
      if( item.key === ACTION_COLUMN_KEY) {
        return {
          ...item,
          render: this.renderActionsCell
        };
      }
      if( item.key === MINUTES_COLUMN_KEY) {
        return {
          ...item,
          render: this.renderMinutesCell
        };
      }
      return {
        ...item,
        render: this.renderDefaultCell
      };
    });
  }

  render() {
    return (
      <div className={cx(styles.tableContainer, styles.withLessPadding)}>
        {this.renderEditRow()}
        <DataTable<IAllocationDisplayItem>
          columns={this.getActiveColumns()}
          data={this.props.items}
          loading={this.props.fetchListRequestStatus === FetchingStatus.Loading}
          editingEntity=""
          withActiveToggle
          sort={{ status: SortStatus.None }}
          loggedInUser={null}
          noDataMessage='No data'
          sortTable={() => undefined}
          toggleEditEntityHandler={() => undefined}
        />
      </div>
    );
  }

}

const mapStateToProps = (state: RootState, props: ExternalProps): IMapStateToProps => {
  const [previousPeriodData] = selectDataOfLastTwoStartPeriods(state);
  const account = accountListSelectors.getAccountById(state, props.accountId);
  const region = getAccountRegion(account?.region);
  return {
    deleteTermRequestStatus: selectDeleteTermRequestStatus(state),
    fetchListRequestStatus: selectListFetchingStatus(state),
    indexPositionOfCurrentTermTypeItem: previousPeriodData?.index || -1,
    items: selectAllocationItemsList(state),
    region
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getManageMinutesList,
  deleteTerm,
  fetchAccountCurrentTerm
};

const MinutesManagementTable = connect(mapStateToProps, mapDispatchToProps)(MinutesManagementTableBase);

export { MinutesManagementTable };
